import React, { useEffect, useRef } from 'react';

import { useLanguage } from '../_hooks/useLanguage';

// Map the app locale (e.g. "pt-BR") to a Cloudflare Turnstile language code.
const TURNSTILE_LANGUAGES = {
    'pt-BR': 'pt-br',
    'es-ES': 'es',
    'en-US': 'en',
};

const Turnstile = ({
    siteKey,
    onVerify,
    onError,
    onExpire,
    className = '',
    theme = 'light',
    size = 'normal',
    resetTrigger = null
}) => {
    const turnstileRef = useRef(null);
    const widgetIdRef = useRef(null);
    const locale = useLanguage();
    const language = TURNSTILE_LANGUAGES[locale] || locale.toLowerCase();

    useEffect(() => {
        // Load Cloudflare Turnstile script if not already loaded
        if (!window.turnstile) {
            const script = document.createElement('script');
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
            script.async = true;
            script.defer = true;
            script.onload = initializeTurnstile;
            document.head.appendChild(script);
        } else {
            initializeTurnstile();
        }

        return () => {
            if (widgetIdRef.current !== null && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        // Reset turnstile when resetTrigger changes
        if (resetTrigger !== null && widgetIdRef.current !== null && window.turnstile) {
            window.turnstile.reset(widgetIdRef.current);
        }
    }, [resetTrigger]);

    useEffect(() => {
        // Re-render the widget when the locale changes so it follows the selected language
        if (widgetIdRef.current !== null && window.turnstile) {
            initializeTurnstile();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    const initializeTurnstile = () => {
        if (!turnstileRef.current || !window.turnstile || !siteKey) {
            return;
        }

        // Remove existing widget if present
        if (widgetIdRef.current !== null) {
            window.turnstile.remove(widgetIdRef.current);
        }

        // Render new widget
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
            sitekey: siteKey,
            callback: onVerify,
            'error-callback': onError,
            'expired-callback': onExpire,
            theme: theme,
            size: size,
            language: language
        });
    };

    return (
        <div 
            ref={turnstileRef}
            className={`turnstile-widget ${className}`}
        />
    );
};

export default Turnstile;